import type { AxiosInstance } from 'axios';
import MANIFEST_STATUSES from '@/components/manifestStatuses';
import type {
  Manifest,
  ManifestListQueryParams,
  ManifestListResponse,
  ManifestCreateRequest,
  ManifestUpdateRequest,
  ManifestSplitRequest,
  EntityId,
  AssetListResponse,
  Asset,
} from '@/types';

export const getManifests = async (
  api: AxiosInstance,
  document_type_id: EntityId,
  limit = 50,
  start_key: string | undefined = undefined,
  name: string | undefined = undefined,
): Promise<ManifestListResponse> => {
  const resp = await api.get<ManifestListResponse>('/manifest', {
    params: { document_type_id, limit, start_key, name } as ManifestListQueryParams,
  });
  return resp.data;
};

export const getActiveManifests = async (
  api: AxiosInstance,
  document_type_id: EntityId,
  limit = 50,
  start_key: string | undefined = undefined,
  name: string | undefined = undefined,
): Promise<ManifestListResponse> => {
  const resp = await api.get<ManifestListResponse>('/manifest/active', {
    params: { document_type_id, limit, start_key, name } as ManifestListQueryParams,
  });
  return resp.data;
};

export const getPendingManifests = async (
  api: AxiosInstance,
  limit = 50,
  start_key: string | undefined = undefined,
  name: string | undefined = undefined,
): Promise<ManifestListResponse> => {
  const resp = await api.get<ManifestListResponse>('/manifest/pending', {
    params: { limit, start_key, name } as ManifestListQueryParams,
  });
  return resp.data;
};

export const getManifest = async (
  api: AxiosInstance,
  manifest_id: EntityId,
): Promise<Manifest> => {
  const resp = await api.get<Manifest>(`/manifest/${manifest_id}`);
  return resp.data;
};

export const createManifest = async (
  api: AxiosInstance,
  values: ManifestCreateRequest,
): Promise<Manifest> => {
  const resp = await api.post<Manifest>(`/manifest/`, values);
  return resp.data;
};

export const updateManifest = async (
  api: AxiosInstance,
  manifest_id: EntityId,
  values: ManifestUpdateRequest,
): Promise<Manifest> => {
  const resp = await api.put<Manifest>(`/manifest/${manifest_id}`, values);
  return resp.data;
};

export const deleteManifest = async (
  api: AxiosInstance,
  manifest_id: EntityId,
): Promise<void> => {
  const resp = await api.delete(`/manifest/${manifest_id}`);
  return resp.data;
};

export const splitManifest = async (
  api: AxiosInstance,
  manifest_id: EntityId,
  values: ManifestSplitRequest,
): Promise<unknown> => {
  const resp = await api.post(`/manifest/${manifest_id}/split`, values);
  return resp.data;
};

export const generateManifestChecks = async (
  api: AxiosInstance,
  manifest_id: EntityId,
): Promise<unknown> => {
  const resp = await api.post(`/manifest/${manifest_id}/generate`, {});
  return resp.data;
};

export const submitManifest = async (
  api: AxiosInstance,
  manifest_id: EntityId,
): Promise<Manifest> => {
  return await updateManifest(api, manifest_id, {
    status: MANIFEST_STATUSES.PENDING,
  });
};

export const approveManifest = async (
  api: AxiosInstance,
  manifest_id: EntityId,
): Promise<Manifest> => {
  return await updateManifest(api, manifest_id, {
    status: MANIFEST_STATUSES.ACTIVE,
  });
};

export const rejectManifest = async (
  api: AxiosInstance,
  manifest_id: EntityId,
): Promise<Manifest> => {
  const resp = await api.put<Manifest>(`/manifest/${manifest_id}`, {
    status: MANIFEST_STATUSES.DRAFT,
  });
  return resp.data;
};

export const getManifestAssets = async (
  api: AxiosInstance,
  manifest_id: EntityId,
): Promise<AssetListResponse> => {
  const resp = await api.get<AssetListResponse>(`/manifest/${manifest_id}/asset`);
  return resp.data;
};

export const getManifestAsset = async (
  api: AxiosInstance,
  manifest_id: EntityId,
  asset_id: EntityId,
): Promise<Asset> => {
  const resp = await api.get<Asset>(
    `/manifest/${manifest_id}/asset/${asset_id}`,
  );
  return resp.data;
};

